/**
 * ACCOUNT-DELETABLE-1 — closing an account, which is not deleting a row.
 *
 * THE DEFECT. A merchant who takes one cash payment can never close their
 * account. Proven on a throwaway against production, in this order:
 *
 *   delete the payment      → manual_payments is append-only … never by delete.
 *   edit the payment        → manual_payments is append-only … never by update.
 *   delete the business     → manual_payments is append-only … never by delete.
 *                             (every child of sub_accounts is ON DELETE CASCADE,
 *                             manual_payments included, so deleting the business
 *                             IS an attempt to destroy the payment)
 *   delete the group        → violates sub_accounts_merchant_group_id_fkey
 *   delete the profile      → ALLOWED, and this is what the product's own
 *                             "Delete my account" button does today
 *   delete the user         → violates a foreign key on auth.users
 *
 * So the append-only law and "delete my account" are not two rules in tension.
 * They are one rule read twice: deleting the business is destroying the ledger.
 *
 * THE SHAPE, AND WHY THIS ONE. Ghost's own Terms already decide it — the
 * licence to hold Customer Data ends when a merchant closes their account
 * "except for records we must keep for legal or audit reasons (consent
 * evidence, financial records, dispute evidence)". Financial records are
 * REQUIRED to outlive the business that made them. No deletion can honour that;
 * a closure can. So closing is a STATE the account enters, not a row that goes.
 *
 * WHAT THIS FILE DECIDES, and what it deliberately does not: it decides what
 * state an account is in, whether it may be closed, and what a closure is
 * allowed to touch. It never writes anything. The write is a database function,
 * printed in the wave record.
 */

/**
 * THE LEDGER. A closure may never write to one of these, in any way.
 *
 * `manual_payments` carries the append-only triggers; the rest are money
 * history the same argument covers. The list exists so that a closure can be
 * asked what it would touch and be REFUSED, rather than relying on a database
 * trigger to catch a mistake nobody meant to make.
 */
export const MONEY_TABLES: ReadonlyArray<string> = [
  'manual_payments',
  'payments',
  'invoices',
  'invoice_line_items',
  'wallet_transactions',
  'affiliate_commission_events',
]

/** The two states an account may be in, and there is no third. */
export const ACCOUNT_STATES = ['open', 'closed'] as const
export type AccountState = (typeof ACCOUNT_STATES)[number]

export interface AccountRow {
  id?: string
  business_name?: string | null
  closed_at?: string | null
  closed_reason?: string | null
}

export interface Lifecycle {
  state: AccountState
  /** When it closed. Null for an open account — not a zero, an absence. */
  since: string | null
  reason: string | null
}

/**
 * A HALF-CLOSED ACCOUNT IS A BUG, NOT A STATE.
 *
 * `closed_at` and `closed_reason` are set together or not at all — the same
 * pairing `manual_payments_reversal_has_reason` already uses for a reversal,
 * and the one `workflows.paused_at`/`paused_reason` uses for a pause. The
 * database CHECK refuses the pair being broken; this names it in words for
 * anything that reads a row the CHECK did not write.
 */
export function halfClosedFault(row: AccountRow | null | undefined): string | null {
  const at = row?.closed_at ?? null
  const why = row?.closed_reason ?? null
  if (at !== null && why === null) {
    return 'the account is marked closed but says nothing about why, so nobody can tell whether it was the merchant or a mistake'
  }
  if (at === null && why !== null) {
    return 'the account carries a closing reason but is not closed, so it is listed and unreachable at once'
  }
  return null
}

/**
 * WHAT STATE IS THIS ACCOUNT IN. Total: every row is open or closed.
 *
 * Throws on a half-state rather than guessing, because guessing is how an
 * account ends up neither open nor closed and nobody notices.
 */
export function lifecycleOf(row: AccountRow | null | undefined): Lifecycle {
  const fault = halfClosedFault(row)
  if (fault) throw new Error(fault)
  const at = row?.closed_at ?? null
  return at === null
    ? { state: 'open', since: null, reason: null }
    : { state: 'closed', since: at, reason: row?.closed_reason ?? null }
}

/** Is this account reachable — listed, switchable, worked in. */
export function isReachable(row: AccountRow | null | undefined): boolean {
  return lifecycleOf(row).state === 'open'
}

/** The accounts a merchant sees. A closed one is gone from every list. */
export function visibleAccounts<T extends AccountRow>(rows: readonly T[]): T[] {
  return rows.filter((r) => isReachable(r))
}

/**
 * WHY THIS ACCOUNT MAY NOT BE CLOSED, or null if it may.
 *
 * A reason is required and must say something. "Closing an account" with an
 * empty reason is the half-state above, arrived at politely.
 */
export function closureFault(row: AccountRow | null | undefined, reason: string): string | null {
  const half = halfClosedFault(row)
  if (half) return half
  if (lifecycleOf(row).state === 'closed') {
    return 'the account is already closed'
  }
  if (reason.trim().length === 0) {
    return 'closing an account needs a reason, so that a closed account can say why'
  }
  return null
}

/**
 * WHAT A CLOSURE WRITES.
 *
 * Returned rather than performed, so it can be asked and refused. Closing
 * writes ONE row: the account's own lifecycle columns. It does not delete the
 * business, the group, the profile or the user, and it touches no money.
 */
export function closurePlan(): ReadonlyArray<string> {
  return ['sub_accounts']
}

/**
 * DOES THIS PLAN TOUCH THE LEDGER. The guard's question, asked of any plan.
 *
 * Kept separate from `closurePlan` on purpose: a future closure that grew a
 * step is checked by the same function, and a plan naming a money table is
 * named as the fault it is rather than silently allowed.
 */
export function ledgerFault(plan: ReadonlyArray<string>): string | null {
  const touched = plan.filter((t) => MONEY_TABLES.includes(t))
  if (touched.length === 0) return null
  return `closing an account may never write to money history, and this would write to ${touched.join(', ')}`
}
